import {
    Burger,
    Button,
    Indicator,
    Skeleton,
    Space,
    Stack,
    Text,
    UnstyledButton,
    useMantineTheme,
} from '@mantine/core'
import { ChevronDown, ShoppingCart, Store } from 'lucide-react'

import StouxLogo from '@/components/atoms/StouxLogo/StouxLogo'
import { SearchBar } from '@/components/organisms/SearchBar'
import { FeatureFlagContext } from '@/contexts/FeatureFlagsContext'
import { LightSearchProvider } from '@/contexts/LightSearchContext'
import { useMediaQuery } from '@mantine/hooks'
import Link from 'next/link'
import { Suspense, useContext, useEffect, useState } from 'react'

import AllProductsDropdownContextProvider from '@/contexts/AllProductsDropdownContext'

import { useStore } from '@/contexts/store-context'
import { useMenuCategories } from '@/hooks/use-store-categories'
import { getStore } from '@/utils/store'
import { FallbackProps } from 'react-error-boundary'
import AllProductsDropdown from './Dropdowns/AllProducts'
import ListStoreModal from './ListStoreModal'
import MyAccount from './MyAccount/MyAccount'
import { useViewModel } from './StoreNavigation.vm'
import MobileMenu from './mobile-menu'
import QuickCategories from './quick-categories'
import { IsMobile } from '@/utils/mobile'

type Props = {
    className?: string
}

const StoreNavigation = (props: Props) => {
    const viewModel = useViewModel()
    const store = getStore()
    // Store front: only the categories where the store has stock
    const { data: categories } = useMenuCategories()

    const { totalItems } = useStore()

    const [showListStoresModal, setShowListStoresModal] = useState(false)

    const { isFeatureEnabled } = useContext(FeatureFlagContext)

    const isMobile = IsMobile();

    const isDesktop = !isMobile;
  

    const itemsCountLabel = () => {
        if (totalItems === 0) {
            return null
        }

        if (totalItems > 9) {
            return '9+'
        }

        return totalItems.toString()
    }

    return (
        <LightSearchProvider>
            <AllProductsDropdownContextProvider>
                <nav className="sticky top-0 z-30 shadow-xl bg-white">
                    <div className="flex flex-col ">
                        <div className="flex items-center justify-between px-5 gap-4">
                            <Link href="/">
                                <StouxLogo className="max-w-[8rem] text-primary transition-all duration-200 ease-out cursor-pointer hover:scale-105 hover:text-btn-hover" />
                            </Link>

                            {categories ? (
                                <AllProductsDropdown categories={categories} />
                            ) : (
                                <Skeleton height={40} width={220} />
                            )}

                            <Suspense>
                                {showListStoresModal && (
                                    <ListStoreModal
                                        opened={showListStoresModal}
                                        close={() =>
                                            setShowListStoresModal(false)
                                        }
                                    />
                                )}
                            </Suspense>

                            {isDesktop && (
                                <>
                                    {!store && (
                                        <div
                                            className="flex flex-row items-center justify-center cursor-pointer gap-2 hover:bg-gray-50 p-2 rounded-md"
                                            onClick={() =>
                                                setShowListStoresModal(true)
                                            }
                                        >
                                            <Store className="text-gray-600" />
                                            <ChevronDown
                                                size={16}
                                                className="text-gray-600"
                                            />
                                        </div>
                                    )}
                                    <SearchBar />
                                    <div className="flex items-center gap-x-4">
                                        {isFeatureEnabled('cart') && (
                                            <UnstyledButton
                                                type="button"
                                                c="gray.6"
                                                className="p-2 transition-all duration-200 ease-out rounded hover:bg-neutral-100"
                                                onClick={viewModel.onOpenCart}
                                            >
                                                <Indicator
                                                    disabled={!totalItems}
                                                    label={itemsCountLabel()}
                                                    inline
                                                    color="red"
                                                    size="16"
                                                >
                                                    <ShoppingCart data-cart-icon="" className="w-6 h-6" />
                                                </Indicator>
                                            </UnstyledButton>
                                        )}

                                        {isFeatureEnabled('account') && (
                                            <MyAccount />
                                        )}

                                        {/* {!store && (
                                            <Link href="/faq/vendre-sur-stoux">
                                                <Button size="xs">
                                                    {isLaptopLarge
                                                        ? 'Vendre'
                                                        : 'Vendre sur STOUX'}
                                                </Button>
                                            </Link>
                                        )} */}
                                    </div>
                                </>
                            )}
                            {!isDesktop && (
                                <Burger
                                    color="var(--primary)"
                                    opened={viewModel.mobileMenuOpened}
                                    onClick={viewModel.openMobileMenu}
                                    aria-label="Ouvrir le menu"
                                />
                            )}
                        </div>
                        {isMobile && (
                            <div className="p-5">
                                <SearchBar />
                            </div>                                           
                        )}
                        <Suspense>
                            <QuickCategories />
                        </Suspense>
                    </div>
                </nav>
                {!isDesktop && (
                        <MobileMenu
                            opened={viewModel.mobileMenuOpened}
                            onClose={viewModel.closeMobileMenu}
                            onOpenCart={viewModel.onOpenCart}
                            onOpenStoreList={() => {
                                viewModel.closeMobileMenu()
                                setShowListStoresModal(true)
                            }}
                        />
          
                )}
            </AllProductsDropdownContextProvider>
        </LightSearchProvider>
    )
}

const Loading = () => {
    const { colors } = useMantineTheme()
    return (
        <nav
            className="relative z-10 isolate bg-white h-full max-h-[6rem] sm:max-h-[7rem] xl:max-h-[8rem]"
            style={{
                border: `1px solid ${colors.gray[2]}`,
            }}
        >
            <div className="py-5">
                <div className="flex items-center justify-between px-6 mx-auto lg:gap-x-8 2xl:gap-x-12 max-w-7xl lg:px-8 ">
                    <Link href="/">
                        <StouxLogo className="max-w-[6rem] lg:max-w-[8rem] xl:max-w-[6rem] text-green-400 transition-all duration-200 ease-out cursor-pointer hover:scale-105 hover:text-btn-hover" />
                    </Link>

                    <div className="items-center justify-between hidden px-6 mx-auto lg:flex lg:flex-row lg:gap-x-8 2xl:gap-x-12 max-w-7xl lg:px-8">
                        <Skeleton width={200} height={40} />
                        <Skeleton width={200} height={40} />
                        <Skeleton width={200} height={40} />
                        <Skeleton width={48} height={48} />
                        <Skeleton width={48} height={48} />
                    </div>
                </div>
            </div>
        </nav>
    )
}

const Error = ({ error, resetErrorBoundary }: FallbackProps) => {
    const { colors } = useMantineTheme()
    return (
        <nav
            className="relative z-10 isolate bg-white h-full max-h-[6rem] sm:max-h-[7rem] xl:max-h-[8rem]"
            style={{
                border: `1px solid ${colors.gray[2]}`,
            }}
        >
            <div className="py-5">
                <div className="flex items-center justify-between px-6 mx-auto lg:gap-x-8 2xl:gap-x-12 max-w-7xl lg:px-8 ">
                    <Link href="/">
                        <StouxLogo className="max-w-[6rem] lg:max-w-[3rem] xl:max-w-[6rem] text-green-400 transition-all duration-200 ease-out cursor-pointer hover:scale-105 hover:text-btn-hover" />
                    </Link>

                    <Stack align="center" className="flex-1 w-full" spacing={2}>
                        <Text size="lg" weight="bold" c="gray.7">
                            Oups!
                        </Text>
                        <Text size="sm" c="gray.6">
                            Une erreur est survenue lors du chargement de la
                            navigation.
                        </Text>
                        <Space h="sm" />
                        <Button
                            size="xs"
                            compact
                            color="gray"
                            variant="white"
                            onClick={() => {
                                resetErrorBoundary()
                            }}
                        >
                            Recharger
                        </Button>
                    </Stack>
                </div>
            </div>
        </nav>
    )
}

export default StoreNavigation
